import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from "react-router-dom";
import { Modal, Tabs, Tab } from 'react-bootstrap';
import Pagination from "react-js-pagination";
import authStore from '../../store/authStore';
import Utils from '../../helpers/Utility';
import LoadingWheel from '../loadingwheel';
import SensorService from '../../services/sensorService';
import SensorboxStateVisualization from '../SensorboxStateVisualization';
import MlAlertLogsTab from '../MlAlertLogsTab';
import MlAlertChartModal, { healthChartToAlertLog } from '../MlAlertChartModal';
import "./alertLogsModal.css"
import '../maintenance-checklist/maintenance-checklist.css';
import moment from 'moment'
import Permissions from '../../config/permssions';
import constants from '../../config/constants';
import Routes from '../../routing/routes';

const maxLimit = 16
const {
    view_only_partial_asset_sensorbox_details,
    create_edit_issue_tracker
} = Permissions

interface Props {
  show: boolean;
  onClose: () => void;
  sensorBoxName?: string;
  sensorBox: any,
  showLogDataModal: (logData: any) => void;
  showInvalidRangeAlertModal:(logData: any) => void;
  lastTab:string;
  onTabChange?: (tab: string) => void;
}

interface ExistingTicketModalProps {
  show: boolean;
  onClose: () => void;
  ticket: any;
  onOpenTicket: () => void;
  onOpenNewTicket: () => void;
}

const AlertLogsModal: React.FC<Props> = ({ show, sensorBoxName, sensorBox, onClose,
  showLogDataModal,showInvalidRangeAlertModal ,lastTab, onTabChange}) => {
  const [loading, setLoading] = useState(true)
  const [alertLogs, setAlertLogs] = useState<Array<any>>([])
  const [deviceLogs, setDeviceLogs] = useState<Array<any>>([])
  const [validRangeLogs, setValidRangeLogs] = useState<Array<any>>([])
  const [currentAlertLogPage, setCurrentAlertLogPage] = useState(1)
  const [currentDeviceLogPage, setCurrentDeviceLogPage] = useState(1)
  const [currentValidRangeLogPage, setCurrentValidRangeLogPage] = useState(1)
  const [alertLogsTotal, setAlertlogsTotal] = useState(0)
  const [deviceLogsTotal, setDeviceLogsTotal] = useState(0)
  const [validRangeLogsTotal, setValidRangeLogsTotal] = useState(0)
  const [existingTicketModal, setExistingTicketModal] = useState(false)
  const [existingTicket, setExistingTicket] = useState<any>(null)
  const [currentAlertLog, setCurrentAlertLog] = useState<any>(null)
  const [activeTab, setActiveTab] = useState(lastTab || 'status')
  const [chartAlertLog, setChartAlertLog] = useState<any>(null)
  const [showChartModal, setShowChartModal] = useState(false)
  const [chartLoading, setChartLoading] = useState(false)
  const chartRequest = useRef(0)

  const history = useHistory();

  const canViewPredictionAlerts = authStore?.isOrgAdmin();

  useEffect(() => {
    const nextTab = lastTab || 'status'
    if (nextTab === 'predictive' && !canViewPredictionAlerts) {
      setActiveTab('status')
      return
    }
    setActiveTab(nextTab)
  }, [lastTab, canViewPredictionAlerts]);

  useEffect(() => {
    getSensorAlertLogs()
    getDeviceAlertLogs()
    getValidRangeLogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorBox, sensorBoxName]);

  const handleTabSelect = (key: string | null) => {
    const nextTab = key || 'status'
    setActiveTab(nextTab)
    onTabChange?.(nextTab)
  }

  const getSensorAlertLogs = async (pageNumber: number = 0) => {
    if (sensorBoxName) {
      setLoading(true)
      pageNumber = pageNumber === 0 ? currentAlertLogPage : pageNumber;
      let response;
      if (sensorBox?.category === "generator") {
        response = await SensorService.getAlertLogs(sensorBoxName, pageNumber, maxLimit)
        setAlertLogs(response.data.logs)
      } else {
        response = await SensorService.getSensorAlertLogs(sensorBoxName, pageNumber, maxLimit)
        setAlertLogs(response.data.results)
      }
      setCurrentAlertLogPage(pageNumber)
      setAlertlogsTotal(response.data.total)
      setLoading(false)
    }
    
  }

    const getDeviceAlertLogs = async (pageNumber: number = 0) => {

    if (sensorBoxName) {
      setLoading(true)
      pageNumber = pageNumber === 0 ? currentDeviceLogPage : pageNumber;
      const response = await SensorService.getDeviceOfflineLogs(sensorBoxName, pageNumber, maxLimit)
      setDeviceLogs(response.data.logs)
      setCurrentDeviceLogPage(pageNumber)
      setDeviceLogsTotal(response.data.total)
      setLoading(false)
    }
    
  }

  const getValidRangeLogs = async (pageNumber: number = 0) => {

    if (sensorBoxName) {
      setLoading(true)
      pageNumber = pageNumber === 0 ? currentValidRangeLogPage : pageNumber;
      const response = await SensorService.getValidRangeLogs(sensorBoxName, pageNumber, maxLimit)
      setValidRangeLogs(response.data.logs)
      setCurrentValidRangeLogPage(pageNumber)
      setValidRangeLogsTotal(response.data.total)
      setLoading(false)
    }
  }

  const checkExistingTicket = async (alertLog: any) => {
    let code = "";
    if (sensorBox?.category === "generator") {
      code = alertLog.diagnostics_code
    } else {
      code = alertLog.triggerCode?.code
    }
    
    try {
      const response = await SensorService.checkExistingTicket(sensorBox?._id || '', code);
      return response;
    } catch (error) {
      console.error('Error checking existing ticket:', error);
      return { exists: false };
    }
  }

  const handleIssueTrackerClick = async (alertLog: any) => {
    const existingTicketData = await checkExistingTicket(alertLog);
    
    if (existingTicketData.exists) {
      setExistingTicket(existingTicketData.ticket);
      setCurrentAlertLog(alertLog);
      setExistingTicketModal(true);
    } else {
      showIssueCreationPage(alertLog);
    }
  }

  const showIssueCreationPage = (alertLog: any) => {
    let orgid = sensorBox.org_id

    let params = `?system_id=${sensorBox?._id}&status=${sensorBox?.status}`

    if(authStore?.isSuperAdmin || (authStore?.isOrgAdmin() && authStore.currentUser.is_default)){
        params += `&org_id=${orgid}`
    }

    let desc = "";
    let color;
    let code = "";
    if (sensorBox?.category === "generator") {
      desc = alertLog.description
      color = constants.statusColor.get(String(alertLog.status))
      code = alertLog.diagnostics_code
    } else {
      let triggerCode = alertLog.triggerCode
      desc = algorithmDescriptionCheck(triggerCode)
      color = triggerCode?.color
      code = triggerCode?.code
    }
    params += `&alert_description=${desc}`
    params += `&alert_code=${code.replace(/#.*$/, '')}`
    params += `&alert_color=${color}`
    
    // Add alert log ID for linking
    if (alertLog._id) {
      params += `&alert_log_id=${alertLog._id}`
    }

    history.push({
      pathname: Routes.issuetrackerdetails,
      search: params,
    });
}
  const demoCheck = (authStore?.currentUser?.active_role?.role_type === -1);
  const canCreateEditTickets = authStore?.currentUser?.access?.includes(create_edit_issue_tracker);
  const algorithmDescriptionCheck = (item: any) => {
    return item?.description
  }

  const recipientList = (emails: any) => {
    const external = (emails?.external || []).filter(Boolean)
    if (external.length === 0) {
      return 'No External Recipients'
    }
    return external.toString().replace(/,/g, ', ')
  }

  const handleOpenExistingTicket = () => {
    if (existingTicket) {
      let params = `?ticket_id=${existingTicket.id}`;
      if (authStore?.isSuperAdmin || (authStore?.isOrgAdmin() && authStore.currentUser.is_default)) {
        params += `&org_id=${sensorBox.org_id}`;
      }
      history.push({
        pathname: Routes.issuetrackerdetails,
        search: params,
      });
    }
    setExistingTicketModal(false);
  }

  const handleCloseExistingTicket = () => {
    setExistingTicketModal(false);
  }

  const handleOpenNewTicket = () => {
    if (currentAlertLog) {
      showIssueCreationPage(currentAlertLog);
      setExistingTicketModal(false);
    }
  }

  const chartTitleName = sensorBox?.alias || sensorBoxName

  const openHealthChart = async (item: any, code: string, description: string, sensorValues?: any) => {
    const requestId = ++chartRequest.current
    const request = { logId: item._id, code, sensorbox: sensorBoxName, description }
    const baseLog = {
      chartTitle: chartTitleName && code ? `${chartTitleName} - ${code}` : (code || chartTitleName),
      healthChartRequest: request,
      sensorValues: sensorValues || null,
      trigger_code: { code, description },
      ml: { chart: { points: [], marker_ms: item.timestamp } }
    }
    setChartAlertLog(baseLog)
    setChartLoading(true)
    setShowChartModal(true)
    try {
      const chart = await SensorService.getHealthAlertChart(item._id, code, sensorBoxName, description)
      if (requestId !== chartRequest.current) return
      setChartAlertLog({
        ...healthChartToAlertLog(chart, chartTitleName, request),
        sensorValues: sensorValues || null,
      })
    } catch (error) {
      if (requestId !== chartRequest.current) return
      setChartAlertLog(baseLog)
    } finally {
      if (requestId === chartRequest.current) setChartLoading(false)
    }
  }

  const closeChartModal = () => {
    chartRequest.current += 1
    setShowChartModal(false)
    setChartAlertLog(null)
    setChartLoading(false)
  }

  let failureModeStyleWidth = '54%'
  if(sensorBox.device_type === 'lockrouter') failureModeStyleWidth = '68%'

  return (
    <>
    <Modal
      show={show && !showChartModal}
      onHide={onClose}
      size="lg"
    >
      <Modal.Header closeButton>
      <div>{sensorBoxName} {sensorBox.alias && <span className='pl-2'>( {sensorBox.alias} )</span> }</div>
      </Modal.Header>
      <Modal.Body>
        <Tabs variant="pills" className="checklist-tab mb-3" activeKey={activeTab} onSelect={handleTabSelect}>
           <Tab eventKey="status" title="Health Alerts">
            {
            <div>
            <div className="table-wrap">
              {loading ?
                <LoadingWheel />
                :
                <table className={alertLogs.length > 0 ? "table custom-table table-style-logs text-custom-size" : "table custom-table table-style-no-logs "} style={{ tableLayout: 'fixed' }}>
                  {alertLogs.length > 0 ? 
                    <>
                      <thead className="table-header-bg">
                      <tr>
                        <th style={{ width: '3%' }} scope="col">#</th>
                        <th style={{ width: '6%' }} className="text-center" scope="col">Status</th>
                        {(sensorBox.device_type === 'general' || sensorBox.device_type === 'cyclictest') && <th style={{ width: '13%' }} className="text-center" scope="col">Diagnostics Code</th> }
                        <th style={{ width: failureModeStyleWidth }} className="text-center" scope="col">Failure Mode</th>
                        <th style={{ width: '18%' }} className="text-center" scope="col">Alert Mail Sent On</th>
                        {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) &&
                          <th style={{ width: '30%' }} className="text-center" scope="col">Recipients</th>
                        }
                        {(canCreateEditTickets && !sensorBox?.sensorbox_group) && (
                          <th style={{ width: '10%' }} className="text-center" scope="col">Insights / Tickets</th>
                        )}
                      </tr>
                      </thead>
                      <tbody>
                        {alertLogs.map((item: any, index) => {
                          let desc = "";
                          let color;
                          let code = "";
                          if (sensorBox?.category === "generator") {
                            desc = item.description
                            color = constants.statusColor.get(String(item.status))
                            code = item.diagnostics_code
                          } else {
                            let triggerCode = item.triggerCode
                            desc = algorithmDescriptionCheck(triggerCode)
                            color = triggerCode?.color
                            code = triggerCode?.code
                          }

                          const result: Record<string, any> = {
                            components: {}
                          };

                          let allComponents: Record<string, any> = {};
                          if (sensorBox?.category === "generator" && item.values) {
                            allComponents = item.values
                          } else if (item.components) {
                            item.components.forEach((comp: any) => {
                              const compType = comp?.comp_type;
                              const seq = comp?.seq;
                              if (!compType) return;
                          
                              Object.entries(comp).forEach(([key, value]) => {
                                if (key !== 'comp_type' && key !== 'seq' && value != null) {
                                  if (seq) {
                                    allComponents[`${compType}_${seq}.${key}`] = value;
                                  } else {
                                    allComponents[`${compType}.${key}`] = value;
                                  }
                                }
                              });
                            });
                          }

                          result.components = allComponents;
                          result.highlightKeys = item.triggerCode?.trigger_pre_req || [];
                          result.alertColor = color;
                          result.alertCode = code?.replace(/#.*$/, '');

                          return (
                            <tr  className={index%2 !== 0 ? "background-color click-icon" : "click-icon"} key={index} onClick={() => {
                              if (sensorBox.device_type !== 'general') return
                              const alertCode = code?.replace(/#.*$/, '') || ''
                              if (sensorBox?.category === 'generator' || !item._id) {
                                showLogDataModal(result)
                                return
                              }
                              openHealthChart(item, alertCode, desc, {
                                values: result.components,
                                highlightKeys: result.highlightKeys,
                                alertColor: result.alertColor,
                                alertCode: result.alertCode,
                              })
                            }}>
                              <td>{(maxLimit * (currentAlertLogPage - 1)) + index + 1}</td>
                              <td className="text-center"><i className={`fa fa-circle ${color}-status`} aria-hidden="true"></i></td>
                              {(sensorBox.device_type === 'general' || sensorBox.device_type === 'cyclictest') && 
                              <td className="text-truncate text-center" title={code}>
                                {code?.replace(/#.*$/, '')}
                              </td>
                              }
                              <td className="text-center" title={desc}>{desc}</td>
                              <td className="text-truncate" title={moment(item?.timestamp).format('MM/DD/YYYY, HH:mm:ss')}>{moment(item?.timestamp).format('MM/DD/YYYY, HH:mm:ss')}</td>
                              {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) &&
                                <td className="text-truncate" title={demoCheck ? `example@gmail.com` : recipientList(item.emails)}>{demoCheck ? `example@gmail.com` : recipientList(item.emails)}</td>
                              }
                              {(canCreateEditTickets && !sensorBox?.sensorbox_group) && (
                                <td className="text-center">
                                  <i
                                      className="fa fa-flag cursor-pointer px-3"
                                      onClick={(e: any) => {
                                      e.stopPropagation();
                                      handleIssueTrackerClick(item);
                                      }}
                                      aria-hidden="true"
                                  ></i>
                                </td>
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </>
                    :
                    <div>
                      <p className='no-logs-text'>No logs found</p>
                    </div>}
                </table>
              }
            </div>
            {alertLogs.length > 0 && !loading &&
            <div className={Math.ceil(alertLogsTotal / maxLimit) > 1 ? "pagination-container-logs" : "pagination-container-no-logs"}>
              {Math.ceil(alertLogsTotal / maxLimit) > 1 ?
                <Pagination
                  hideNavigation={true}
                  activePage={currentAlertLogPage}
                  itemsCountPerPage={maxLimit}
                  totalItemsCount={alertLogsTotal}
                  itemClass={"item-class"}
                  activeClass={"active-class"}
                  pageRangeDisplayed={5}
                  onChange={(pageNumber) => getSensorAlertLogs(pageNumber)}
                />
                : null
              }
            </div>
          }
          </div>
          }
          </Tab>
          {sensorBox?.category !== "generator" && !sensorBox?.sensorbox_group && canViewPredictionAlerts && (
            <Tab eventKey="predictive" title="Prediction Alerts">
              <MlAlertLogsTab
                assetId={sensorBoxName}
                sensorBox={sensorBox}
                onShowChart={(alertLog) => {
                  setChartLoading(false);
                  setChartAlertLog(alertLog);
                  setShowChartModal(true);
                }}
                useSensorboxIssueTracker={true}
              />
            </Tab>
          )}
          <Tab eventKey="device" title="Offline Alerts">
          {
            <div>
            <div className="table-wrap">
              {loading ?
                <LoadingWheel />
                :
                <table className={deviceLogs.length > 0 ? "table custom-table table-style-logs text-custom-size" : "table custom-table table-style-no-logs "} style={{ tableLayout: 'fixed' }}>
                  {deviceLogs.length > 0 ? 
                    <>
                      <thead className="table-header-bg">
                       {authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) ?
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">Alert Mail Sent On</th>
                        </tr>
                          :
                        <tr>
                          <th style={{ width: '10%' }} scope="col">#</th>
                          <th style={{ width: '50%' }} scope="col">Recipients</th>
                          <th style={{ width: '40%' }} scope="col">Alert Mail Sent On</th>
                        </tr>
                        }
                      </thead>
                      <tbody>
                        {deviceLogs.map((item: any, index) => {
                          return (
                            <tr  className={index%2 !== 0 ? "background-color click-icon" : "click-icon"} key={index}>
                              <td>{(maxLimit * (currentDeviceLogPage - 1)) + index + 1}</td>
                              {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) &&
                                <td className="text-truncate" title={demoCheck ? `example@gmail.com` : item.emails.toString().replace(/,/g, ', ')}>{demoCheck ? `example@gmail.com` : item.emails.toString().replace(/,/g, ', ')}</td>
                              }
                              <td className="text-truncate" title={moment(item?.timestamp).format('MM/DD/YYYY, HH:mm:ss')} >{moment(item?.timestamp).format('MM/DD/YYYY, HH:mm:ss')}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </>
                    :
                    <div>
                      <p className='no-logs-text'>No logs found</p>
                    </div>}
                </table>
              }
            </div>
            {deviceLogs.length > 0 && !loading &&
            <div className={Math.ceil(deviceLogsTotal / maxLimit) > 1 ? "pagination-container-logs" : "pagination-container-no-logs"}>
              {Math.ceil(deviceLogsTotal / maxLimit) > 1 ?
                <Pagination
                  hideNavigation={true}
                  activePage={currentDeviceLogPage}
                  itemsCountPerPage={maxLimit}
                  totalItemsCount={deviceLogsTotal}
                  itemClass={"item-class"}
                  activeClass={"active-class"}
                  pageRangeDisplayed={5}
                  onChange={(pageNumber) => getDeviceAlertLogs(pageNumber)}
                />
                : null
              }
            </div>
          }
          </div>
          }
          </Tab>
          <Tab eventKey="invalid-range" title="Out of Range Alerts">
          {
            <div>
            <div className="table-wrap">
              {loading ?
                <LoadingWheel />
                :
                <table className={validRangeLogs.length > 0 ? "table custom-table table-style-logs text-custom-size" : "table custom-table table-style-no-logs "} style={{ tableLayout: 'fixed' }}>
                  {validRangeLogs.length > 0 ? 
                    <>
                      <thead className="table-header-bg">
                        {authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) ?
                        <tr>
                          <th scope="col">#</th>
                          <th className="text-center" scope="col">Status</th>
                          <th scope="col">Representations</th>
                          <th scope="col">Alert Mail Sent On</th>
                        </tr>
                        :
                        <tr>
                          <th style={{ width: '5%' }} scope="col">#</th>
                          <th style={{ width: '10%' }} className="text-center" scope="col">Status</th>
                          <th style={{ width: '35%' }} scope="col">Representations</th>
                          <th style={{ width: '35%' }} scope="col">Recipients</th>
                          <th style={{ width: '15%' }} scope="col">Alert Mail Sent On</th>
                        </tr>
                        }
                      </thead>
                      <tbody>
                        {validRangeLogs.map((item: any, index) => {
                          return (
                            <tr  className={index%2 !== 0 ? "background-color click-icon" : "click-icon"} key={index} onClick={() => { sensorBox.device_type === 'general' && showInvalidRangeAlertModal(item) }}>
                              <td>{(maxLimit * (currentValidRangeLogPage - 1)) + index + 1}</td>
                              <td className="text-center"><i className={`fa fa-circle ${Utils.getSensorStatus(item.status)}`} aria-hidden="true"></i></td>
                              <td className="text-truncate" title={item?.representation.join(', ')}>{item?.representation.join(', ')}</td>
                              {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) &&
                                <td className="text-truncate" title={demoCheck ? `example@gmail.com` : item.emails.toString().replace(/,/g, ', ')}>{demoCheck ? `example@gmail.com` : item.emails.toString().replace(/,/g, ', ')}</td>
                              }
                              <td className="text-truncate" title={moment(item?.timestamp).format('MM/DD/YYYY, HH:mm:ss')} >{moment(item?.timestamp).format('MM/DD/YYYY, HH:mm:ss')}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </>
                    :
                    <tr>
                      <p className='no-logs-text'>No logs found</p>
                    </tr>}
                </table>
              }
            </div>
            {validRangeLogs.length > 0 && !loading &&
            <div className={Math.ceil(validRangeLogsTotal / maxLimit) > 1 ? "pagination-container-logs" : "pagination-container-no-logs"}>
              {Math.ceil(validRangeLogsTotal / maxLimit) > 1 ?
                <Pagination
                  hideNavigation={true}
                  activePage={currentValidRangeLogPage}
                  itemsCountPerPage={maxLimit}
                  totalItemsCount={validRangeLogsTotal}
                  itemClass={"item-class"}
                  activeClass={"active-class"}
                  pageRangeDisplayed={5}
                  onChange={(pageNumber) => getValidRangeLogs(pageNumber)}
                />
                : null
              }
            </div>
          }
          </div>
          }
          </Tab>
          <Tab eventKey="connectivity" title="Sensorbox Status">
          {
            <div>
              {sensorBoxName && (
                <SensorboxStateVisualization sensorboxName={sensorBoxName} sensorboxId={sensorBox?._id} />
              )}
            </div>
          }
          </Tab>
        </Tabs>

      </Modal.Body>
      <>
        <ExistingTicketModal
          show={existingTicketModal}
          onClose={handleCloseExistingTicket}
          ticket={existingTicket}
          onOpenTicket={handleOpenExistingTicket}
          onOpenNewTicket={handleOpenNewTicket}
        />
      </>
    </Modal>
    <MlAlertChartModal
      show={showChartModal}
      loading={chartLoading}
      onClose={closeChartModal}
      alertLog={chartAlertLog}
      sensorBoxName={sensorBox?.alias || sensorBoxName}
    />
    </>
  )
}

const ExistingTicketModal: React.FC<ExistingTicketModalProps> = ({ show, onClose, ticket, onOpenTicket, onOpenNewTicket }) => {
  return (
    <Modal show={show} onHide={onClose} size="sm" centered>
      <Modal.Header closeButton>
        <Modal.Title>Ticket: {ticket?.issue_id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>Title:</strong> {ticket?.title}
        </div>
        <div className="mb-3">
          <strong>Status:</strong> <span className={`badge badge-${ticket?.status === 'OPEN' ? 'warning' : ticket?.status === 'RESOLVED' ? 'success' : 'secondary'}`}>{ticket?.status}</span>
        </div>
        <div className="mb-3">
          <strong>Last Updated:</strong> {moment(ticket?.updated_on || ticket?.created_on).format('MM/DD/YYYY - HH:mm:ss')}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onOpenNewTicket}>
          Open New Ticket
        </button>
        <button className="btn btn-primary" onClick={onOpenTicket}>
          View Ticket
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AlertLogsModal;